/* External */
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { endOfDay, isEqual, isSameDay } from 'date-fns';

/* Utils */
import useChannelSchedules from './useChannelSchedules';
import { useCurrentTime } from '../providers/CurrentTimeProvider';
import { shallowEqualLists } from '../utils/functions';

export default function useCurrentAndNextSchedules(channel, numberOfSchedules, time) {
  const currentTime = useCurrentTime();
  const schedules = useChannelSchedules(channel);
  const timeToUse = useMemo(
    () => time || new Date(currentTime * 60_000),
    [currentTime, time],
  );
  const timeToUseRef = useRef();

  const sortedSchedules = useMemo(() => schedules?.sort(
    (scheduleA, scheduleB) => scheduleA.startTime - scheduleB.startTime,
  ), [schedules]);

  const getCurrentAndNextSchedules = useCallback((previous) => {
    let schedulesToReturn = sortedSchedules?.skipUntil(
      ({ startTime, endTime }) => (
        startTime <= timeToUseRef.current && timeToUseRef.current < endTime
      ),
    );

    /* A past/future day's list is capped at that day's end: the channel's
       schedules map is a union of every fetched window (today's page-load
       data lingers), so without the cap the row spills into the NEXT day's
       programs once the picked day's own data runs out — the "skips a
       whole day" walk (e.g. yesterday 17:20 straight to today 09:00).
       Today is not capped: the viewport lookahead is intentional. */
    if (timeToUseRef.current && !isSameDay(
      new Date(timeToUseRef.current),
      new Date(),
    )) {
      const dayEndMs = endOfDay(new Date(timeToUseRef.current)).getTime();
      schedulesToReturn = schedulesToReturn?.takeWhile(
        (schedule) => !!schedule && new Date(schedule.startTime).getTime() < dayEndMs,
      );
    }

    if (numberOfSchedules) {
      schedulesToReturn = schedulesToReturn?.slice(0, numberOfSchedules);
    }

    if (previous && shallowEqualLists(previous, schedulesToReturn)) {
      return previous;
    }

    return schedulesToReturn;
  }, [sortedSchedules, numberOfSchedules, timeToUseRef]);

  const [
    currentAndNextSchedules,
    setCurrentAndNextSchedules,
  ] = useState(getCurrentAndNextSchedules);

  useEffect(() => {
    const first = currentAndNextSchedules?.first();

    if (
      !first
      || first.channelId !== channel?.channelId
      || first.endTime < timeToUse
      || currentAndNextSchedules?.size < numberOfSchedules
      || !isEqual(timeToUseRef.current, timeToUse)
    ) {
      timeToUseRef.current = timeToUse;
      setCurrentAndNextSchedules(getCurrentAndNextSchedules);
    }
  }, [
    getCurrentAndNextSchedules,
    currentAndNextSchedules,
    channel?.channelId,
    timeToUse,
    numberOfSchedules,
  ]);

  return currentAndNextSchedules;
}
